
import { v4 as uuidv4 } from 'uuid';
import { reportsFileStorageService, Report } from './fileStorageService';
import { toast } from 'sonner';

// Enhanced localStorage-only reports service with 5000+ capacity
export const reportsService = {
  async getAllReports(): Promise<Report[]> {
    try {
      console.log('📊 Loading reports from localStorage only (5000+ capacity)...');
      return await reportsFileStorageService.getAllReports();
    } catch (error) {
      console.error('Error loading reports:', error);
      throw error;
    }
  },

  async getReportById(id: string): Promise<Report | undefined> {
    try {
      return await reportsFileStorageService.getReportById(id);
    } catch (error) {
      console.error(`Failed to get report ${id}:`, error);
      return undefined;
    }
  },

  async createReport(reportData: Omit<Report, 'id' | 'createdAt' | 'updatedAt'>): Promise<Report> {
    try {
      console.log('📊 Creating report in localStorage with enhanced persistence...');
      const report = await reportsFileStorageService.addReport(reportData);
      toast.success('Report created successfully');
      return report;
    } catch (error) {
      console.error('Failed to create report:', error);
      toast.error('Failed to create report');
      throw error;
    }
  },

  async updateReport(id: string, reportData: Partial<Omit<Report, 'id' | 'createdAt'>>): Promise<Report> {
    try {
      console.log('📊 Updating report in localStorage with enhanced persistence...');
      const updatedReport = await reportsFileStorageService.updateReport(id, reportData);
      toast.success('Report updated successfully');
      return updatedReport;
    } catch (error) {
      console.error(`Failed to update report ${id}:`, error);
      toast.error('Failed to update report');
      throw error;
    }
  },

  async deleteReport(id: string): Promise<void> {
    try {
      console.log('📊 Deleting report from localStorage...');
      await reportsFileStorageService.deleteReport(id);
      toast.success('Report deleted successfully');
    } catch (error) {
      console.error(`Failed to delete report ${id}:`, error);
      toast.error('Failed to delete report');
      throw error;
    }
  },

  async saveReportAttachment(reportId: string, file: File): Promise<string> {
    try {
      console.log('📊 Saving report attachment to localStorage...');
      const attachmentId = await reportsFileStorageService.saveReportAttachment(reportId, file);
      toast.success('Attachment saved successfully');
      return attachmentId;
    } catch (error) {
      console.error('Failed to save report attachment:', error);
      toast.error('Failed to save attachment');
      throw error;
    }
  },

  // Generate analytics reports with localStorage storage
  async generateAnalyticsReport(type: 'student' | 'teacher' | 'financial', dateRange: { start: string; end: string }): Promise<Report> {
    try {
      console.log('📊 Generating analytics report with localStorage storage...');
      
      const now = new Date().toISOString();
      const data = await collectAnalyticsData(type, dateRange);
      
      const reportData = {
        title: `${type.charAt(0).toUpperCase() + type.slice(1)} Analytics Report`,
        type: type as Report['type'],
        description: `Analytics report for ${type} data from ${dateRange.start} to ${dateRange.end}`,
        generatedBy: 'System',
        generatedAt: now,
        startDate: dateRange.start,
        endDate: dateRange.end,
        format: 'json' as const,
        data: data,
        metadata: {
          totalRecords: Array.isArray(data) ? data.length : Object.keys(data).length,
          filters: { dateRange, type },
          summary: generateSummary(data, type)
        }
      };

      const report = await reportsFileStorageService.addReport(reportData);
      toast.success(`Analytics report generated: ${report.title}`);
      return report;
    } catch (error) {
      console.error('Failed to generate analytics report:', error);
      toast.error('Failed to generate analytics report');
      throw error;
    }
  },

  // Always returns 'offline' since we're localStorage-only
  getSupabaseSyncStatus(): 'synced' | 'pending' | 'offline' {
    return 'offline';
  },

  getPendingSyncItems(): string[] {
    return [];
  }
};

// Helper function to collect analytics data from localStorage
async function collectAnalyticsData(type: string, dateRange: { start: string; end: string }): Promise<any> {
  const { universalStorage } = await import('../../storage/universalStorage');
  
  switch (type) {
    case 'student':
      return await universalStorage.getItem('nalc_students') || [];
    case 'teacher':
      return await universalStorage.getItem('nalc_teachers') || [];
    case 'financial':
      return {
        payments: await universalStorage.getItem('nalc_payments') || {},
        revenue: await universalStorage.getItem('nalc_revenue') || [],
        expenses: await universalStorage.getItem('nalc_expenses') || []
      };
    default:
      return {};
  }
}

// Helper function to generate summary for analytics
function generateSummary(data: any, type: string): any {
  if (type === 'student' && Array.isArray(data)) {
    return {
      totalStudents: data.length,
      activePrograms: data.reduce((acc, student) => acc + (student.programs?.length || 0), 0)
    };
  } else if (type === 'teacher' && Array.isArray(data)) {
    return {
      totalTeachers: data.length,
      departments: [...new Set(data.map(teacher => teacher.department).filter(Boolean))]
    };
  } else if (type === 'financial') {
    return {
      totalPayments: Object.keys(data.payments || {}).length,
      totalRevenue: (data.revenue || []).length
    };
  }
  return {};
}
